/* LabMilo · lm-video-slider · slider horizontal de vídeos 9:16, sin librerías.
   - Custom element <lm-video-slider>: se inicia solo al entrar en la página (también al
     re-renderizarse la sección en el editor) y se limpia al salir.
   - Solo se reproducen los vídeos visibles (IntersectionObserver); el resto, en pausa.
   - Scroll-snap nativo; flechas y puntos mueven la pista a las posiciones de encaje reales.
   - Si los puntos no caben en una fila (p. ej. 15 vídeos en móvil a 390 px), en su lugar sale
     un contador «n / N» (aria-live) con una barra de progreso (decorativa); los puntos no se encogen
     por debajo de 24 px (WCAG 2.5.8). El ancho disponible se mide en el carrusel (no cambia con el modo).
   - Aviso «Desliza» (móvil, CSS): su flecha se anima cuando un vídeo ya está a la vista (clase is-seen)
     y el aviso se va al primer deslizamiento (clase is-scrolled).
   - Pausa global accesible, «reducir movimiento» y ahorro de datos respetados; si el navegador
     bloquea el autoplay (p. ej. iOS en modo de bajo consumo), sale el botón de reproducir. */
(function () {
  'use strict';

  if (!('customElements' in window) || window.customElements.get('lm-video-slider')) return;

  var motionQuery = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;

  function prefersReducedMotion() {
    return !!(motionQuery && motionQuery.matches);
  }

  function saveData() {
    var connection = navigator.connection;
    return !!(connection && connection.saveData);
  }

  class LmVideoSlider extends HTMLElement {
    constructor() {
      super();
      this.lmReady = false;
      this.handleClick = this.handleClick.bind(this);
      this.handlePlaying = this.handlePlaying.bind(this);
      this.handleScroll = this.handleScroll.bind(this);
      this.handleResize = this.handleResize.bind(this);
      this.handleVisibility = this.handleVisibility.bind(this);
      this.handleBlockSelect = this.handleBlockSelect.bind(this);
      this.handleIntersect = this.handleIntersect.bind(this);
    }

    connectedCallback() {
      if (this.lmReady) return;
      this.track = this.querySelector('[data-lm-vs-track]');
      if (!this.track) return;
      this.lmReady = true;

      this.slides = Array.prototype.slice.call(this.track.querySelectorAll('[data-lm-vs-slide]'));
      this.items = [];
      for (var i = 0; i < this.slides.length; i++) {
        var video = this.slides[i].querySelector('video');
        if (video) this.items.push({ slide: this.slides[i], video: video, visible: false });
      }
      this.prevBtn = this.querySelector('[data-lm-vs-prev]');
      this.nextBtn = this.querySelector('[data-lm-vs-next]');
      this.dotsWrap = this.querySelector('[data-lm-vs-dots]');
      this.toggleBtn = this.querySelector('[data-lm-vs-toggle]');
      this.carousel = this.querySelector('[data-lm-vs-carousel]');
      this.controls = this.querySelector('[data-lm-vs-controls]');
      this.barFill = null;
      this.barValue = '';
      this.dots = [];
      this.dotsKey = null;
      /* Se guardan una vez: en modo contador se quitan del contenedor y hay que poder devolverlos
         aunque el elemento se desconecte y se vuelva a conectar (editor). */
      if (this.dotsWrap) {
        this.dotsRole = this.dotsRole || this.dotsWrap.getAttribute('role') || 'group';
        this.dotsLabel = this.dotsLabel || this.dotsWrap.getAttribute('aria-label');
      }
      this.counter = null;
      this.counterActive = -1;
      this.positions = [];
      this.maxScroll = 0;
      this.scrollFrame = 0;
      this.resizeFrame = 0;

      this.wantPlay = this.dataset.autoplay === 'true' && !prefersReducedMotion() && !saveData();

      this.addEventListener('click', this.handleClick);
      this.addEventListener('playing', this.handlePlaying, true);

      this.initVideos();
      this.initNav();
      this.renderToggle();
      this.classList.add('is-ready');
    }

    disconnectedCallback() {
      if (!this.lmReady) return;
      this.lmReady = false;
      this.removeEventListener('click', this.handleClick);
      this.removeEventListener('playing', this.handlePlaying, true);
      this.track.removeEventListener('scroll', this.handleScroll);
      window.removeEventListener('resize', this.handleResize);
      document.removeEventListener('visibilitychange', this.handleVisibility);
      document.removeEventListener('shopify:block:select', this.handleBlockSelect);
      if (this.io) this.io.disconnect();
      if (this.ro) this.ro.disconnect();
      this.io = null;
      this.ro = null;
      if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
      if (this.resizeFrame) cancelAnimationFrame(this.resizeFrame);
      this.scrollFrame = 0;
      this.resizeFrame = 0;
      this.items.forEach(function (item) {
        try {
          item.video.pause();
        } catch (e) {
          /* nada */
        }
      });
    }

    /* ---------- Vídeos ---------- */

    initVideos() {
      var self = this;
      this.items.forEach(function (item) {
        var v = item.video;
        v.muted = true;
        v.defaultMuted = true;
        v.loop = true;
        v.playsInline = true;
        v.setAttribute('playsinline', '');
        v.setAttribute('muted', '');
        item.slide.classList.toggle('is-paused', !self.wantPlay);
      });
      if (!this.items.length) return;

      if ('IntersectionObserver' in window) {
        this.io = new IntersectionObserver(this.handleIntersect, { threshold: [0, 0.25, 0.5, 0.75, 1] });
        this.items.forEach(function (item) {
          self.io.observe(item.video);
        });
      } else {
        this.classList.add('is-seen');
        this.items.forEach(function (item) {
          item.visible = true;
          self.sync(item);
        });
      }
      document.addEventListener('visibilitychange', this.handleVisibility);
    }

    findItem(el) {
      for (var i = 0; i < this.items.length; i++) {
        if (this.items[i].video === el || this.items[i].slide === el) return this.items[i];
      }
      return null;
    }

    isMostlyVisible(entry) {
      if (!entry.isIntersecting) return false;
      if (entry.intersectionRatio >= 0.5) return true;
      /* Tarjeta más alta que la ventana: basta con media anchura y media altura de ventana. */
      var rect = entry.intersectionRect;
      var box = entry.boundingClientRect;
      var viewH = window.innerHeight || document.documentElement.clientHeight || 0;
      return box.width > 0 && rect.width / box.width >= 0.5 && viewH > 0 && rect.height >= viewH * 0.5;
    }

    handleIntersect(entries) {
      for (var i = 0; i < entries.length; i++) {
        var entry = entries[i];
        var item = this.findItem(entry.target);
        if (!item) continue;
        if (entry.isIntersecting && item.video.preload === 'none') item.video.preload = 'metadata';
        item.visible = this.isMostlyVisible(entry);
        /* Primer vídeo a la vista: arranca la animación del aviso «Desliza» (antes se gastaba fuera de pantalla). */
        if (item.visible && !this.classList.contains('is-seen')) this.classList.add('is-seen');
        this.sync(item);
      }
    }

    sync(item) {
      var shouldPlay =
        this.wantPlay && item.visible && !document.hidden && !item.slide.classList.contains('is-paused');
      if (shouldPlay) {
        if (item.video.paused) this.play(item);
      } else if (!item.video.paused) {
        item.video.pause();
      }
    }

    play(item) {
      var promise;
      try {
        promise = item.video.play();
      } catch (e) {
        return;
      }
      if (promise && typeof promise.catch === 'function') {
        promise.catch(function (err) {
          /* Autoplay bloqueado: este vídeo queda en pausa con su botón de reproducir.
             AbortError (una pausa interrumpe la carga) no es un fallo. */
          if (err && err.name === 'NotAllowedError') item.slide.classList.add('is-paused');
        });
      }
    }

    handlePlaying(event) {
      var item = this.findItem(event.target);
      if (item) item.slide.classList.add('is-started');
    }

    handleVisibility() {
      for (var i = 0; i < this.items.length; i++) this.sync(this.items[i]);
    }

    playAll(item, button) {
      var refocus = !!(button && document.activeElement === button);
      this.wantPlay = true;
      this.items.forEach(function (it) {
        it.slide.classList.remove('is-paused');
      });
      this.renderToggle();
      if (refocus) {
        /* El botón pulsado desaparece: el foco pasa al de pausa (o a la pista). */
        var target = this.toggleBtn || this.track;
        try {
          target.focus({ preventScroll: true });
        } catch (e) {
          target.focus();
        }
      }
      /* Dentro del mismo clic: cuenta como gesto del usuario para el navegador. */
      for (var i = 0; i < this.items.length; i++) this.sync(this.items[i]);
      if (item && item.video.paused) this.play(item);
    }

    pauseAll() {
      this.wantPlay = false;
      this.items.forEach(function (it) {
        it.slide.classList.add('is-paused');
        if (!it.video.paused) it.video.pause();
      });
      this.renderToggle();
    }

    renderToggle() {
      if (!this.toggleBtn) return;
      var label = this.wantPlay ? this.dataset.labelPause : this.dataset.labelPlay;
      if (label) this.toggleBtn.setAttribute('aria-label', label);
      this.toggleBtn.classList.toggle('is-paused', !this.wantPlay);
    }

    /* ---------- Navegación ---------- */

    initNav() {
      this.track.addEventListener('scroll', this.handleScroll, { passive: true });
      if ('ResizeObserver' in window) {
        this.ro = new ResizeObserver(this.handleResize);
        this.ro.observe(this.track);
      } else {
        window.addEventListener('resize', this.handleResize);
      }
      document.addEventListener('shopify:block:select', this.handleBlockSelect);
      this.measure();
    }

    handleScroll() {
      if (this.scrollFrame) return;
      var self = this;
      this.scrollFrame = requestAnimationFrame(function () {
        self.scrollFrame = 0;
        self.updateNav();
      });
    }

    handleResize() {
      if (this.resizeFrame) return;
      var self = this;
      this.resizeFrame = requestAnimationFrame(function () {
        self.resizeFrame = 0;
        self.measure();
      });
    }

    paddingStart() {
      return parseFloat(window.getComputedStyle(this.track).paddingLeft) || 0;
    }

    slidePosition(slide, padStart, max) {
      return Math.min(Math.max(slide.offsetLeft - padStart, 0), max);
    }

    /* Posiciones de encaje reales (una por vídeo, sin repetir las que el final de la pista junta). */
    measure() {
      var track = this.track;
      var max = Math.max(0, track.scrollWidth - track.clientWidth);
      var padStart = this.paddingStart();
      var positions = [];
      for (var i = 0; i < this.slides.length; i++) {
        var pos = this.slidePosition(this.slides[i], padStart, max);
        var last = positions[positions.length - 1];
        if (!last || Math.abs(pos - last.pos) > 4) positions.push({ pos: pos, index: i });
      }
      if (positions.length && max - positions[positions.length - 1].pos > 4) {
        positions.push({ pos: max, index: this.slides.length - 1 });
      }
      this.positions = positions;
      this.maxScroll = max;
      this.classList.toggle('is-static', positions.length < 2);
      this.renderDots();
      this.updateNav();
    }

    /* Ancho para los puntos: el del carrusel menos lo que ocupa el resto de la píldora (relleno, borde,
       botón de pausa y hueco). No depende del modo actual (puntos o contador), así que no oscila.
       Sin píldora (marcado antiguo), el ancho del propio contenedor de puntos. */
    dotsAvailable() {
      if (!this.carousel || !this.controls) return this.dotsWrap.clientWidth || 0;
      var width = this.carousel.clientWidth || 0;
      if (width <= 0) return 0;
      var cs = window.getComputedStyle(this.controls);
      var reserved =
        (parseFloat(cs.paddingLeft) || 0) +
        (parseFloat(cs.paddingRight) || 0) +
        (parseFloat(cs.borderLeftWidth) || 0) +
        (parseFloat(cs.borderRightWidth) || 0);
      if (this.toggleBtn) {
        /* Ancho calculado del CSS (4,4 rem): vale aunque el botón aún esté oculto (antes de is-ready). */
        var toggleW = parseFloat(window.getComputedStyle(this.toggleBtn).width) || this.toggleBtn.offsetWidth || 0;
        reserved += toggleW + (parseFloat(cs.columnGap) || 0);
      }
      return width - reserved;
    }

    /* ¿Caben los puntos en UNA fila? Cada punto es una zona táctil de 2,4 rem (24 px con la base
       de 62,5 %). Si no se puede medir (ancho 0), se admiten como mucho 10. */
    dotsFit(count) {
      var available = this.dotsAvailable();
      if (available <= 0) return count <= 10;
      var root = parseFloat(window.getComputedStyle(document.documentElement).fontSize) || 10;
      return count * root * 2.4 <= available + 0.5;
    }

    renderDots() {
      if (!this.dotsWrap) return;
      var count = this.positions.length;
      var mode = count < 2 ? 'none' : this.dotsFit(count) ? 'dots' : 'count';
      var key =
        mode +
        ':' +
        this.positions
          .map(function (p) {
            return p.index;
          })
          .join(',');
      if (key === this.dotsKey) return;
      this.dotsKey = key;
      this.dotsWrap.textContent = '';
      this.dots = [];
      this.counter = null;
      this.counterActive = -1;
      this.barFill = null;
      this.barValue = '';
      this.dotsWrap.classList.toggle('is-count', mode === 'count');

      if (mode === 'count') {
        /* Contador «n / N»: ya no es un grupo de controles, así que se quitan role y nombre del grupo. */
        this.dotsWrap.removeAttribute('role');
        this.dotsWrap.removeAttribute('aria-label');
        var counter = document.createElement('span');
        counter.className = 'lm-vs__count';
        counter.setAttribute('aria-live', 'polite');
        counter.setAttribute('aria-atomic', 'true');
        var visible = document.createElement('span');
        visible.setAttribute('aria-hidden', 'true');
        var spoken = document.createElement('span');
        spoken.className = 'lm-vs__sr';
        counter.appendChild(visible);
        counter.appendChild(spoken);
        this.counter = { el: counter, visible: visible, spoken: spoken };
        /* Barra de progreso: decorativa, el contador ya lo dice. */
        var bar = document.createElement('span');
        bar.className = 'lm-vs__bar';
        bar.setAttribute('aria-hidden', 'true');
        var fill = document.createElement('span');
        fill.className = 'lm-vs__bar-fill';
        bar.appendChild(fill);
        this.barFill = fill;
        /* Texto puesto antes de insertarlo: el lector no lo anuncia al cargar, solo al cambiar. */
        this.renderCount(this.activeIndex());
        this.renderProgress();
        this.dotsWrap.appendChild(counter);
        this.dotsWrap.appendChild(bar);
        return;
      }

      if (this.dotsRole) this.dotsWrap.setAttribute('role', this.dotsRole);
      if (this.dotsLabel) this.dotsWrap.setAttribute('aria-label', this.dotsLabel);
      if (mode === 'none') return;
      var template = this.dataset.labelDot || 'Ir al vídeo [n]';
      for (var i = 0; i < this.positions.length; i++) {
        var dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'lm-vs__dot';
        dot.setAttribute('data-lm-vs-dot', String(i));
        if (this.track.id) dot.setAttribute('aria-controls', this.track.id);
        dot.setAttribute('aria-label', template.replace('[n]', String(this.positions[i].index + 1)));
        this.dotsWrap.appendChild(dot);
        this.dots.push(dot);
      }
    }

    renderCount(active) {
      if (!this.counter || active === this.counterActive) return;
      this.counterActive = active;
      var n = String(active + 1);
      var total = String(this.positions.length);
      var template = this.dataset.labelCount || 'Vídeo [n] de [total]';
      this.counter.visible.textContent = n + ' / ' + total;
      this.counter.spoken.textContent = template.replace('[n]', n).replace('[total]', total);
    }

    /* Barra: 1/N al principio y llena al final, proporcional al desplazamiento (sigue al dedo). */
    renderProgress() {
      if (!this.barFill) return;
      var count = this.positions.length;
      var ratio = this.maxScroll > 0 ? Math.min(Math.max(this.track.scrollLeft / this.maxScroll, 0), 1) : 1;
      var progress = count > 1 ? (1 + (count - 1) * ratio) / count : 1;
      var value = 'scaleX(' + progress.toFixed(4) + ')';
      if (value === this.barValue) return;
      this.barValue = value;
      this.barFill.style.transform = value;
    }

    activeIndex() {
      var x = this.track.scrollLeft;
      var active = 0;
      var best = Infinity;
      for (var i = 0; i < this.positions.length; i++) {
        var d = Math.abs(this.positions[i].pos - x);
        if (d < best) {
          best = d;
          active = i;
        }
      }
      return active;
    }

    updateNav() {
      var x = this.track.scrollLeft;
      var active = this.activeIndex();
      for (var i = 0; i < this.dots.length; i++) {
        var on = i === active;
        this.dots[i].classList.toggle('is-active', on);
        if (on) this.dots[i].setAttribute('aria-current', 'true');
        else this.dots[i].removeAttribute('aria-current');
      }
      this.renderCount(active);
      this.renderProgress();
      /* El aviso «Desliza» se va en cuanto la pista se mueve (dedo, flechas, puntos o teclado). */
      if (x > 8 && !this.classList.contains('is-scrolled')) this.classList.add('is-scrolled');
      /* aria-disabled (no disabled) para no perder el foco del teclado al llegar a un extremo. */
      if (this.prevBtn) this.prevBtn.setAttribute('aria-disabled', String(x <= 2));
      if (this.nextBtn) this.nextBtn.setAttribute('aria-disabled', String(x >= this.maxScroll - 2));
    }

    scrollToX(left) {
      var behavior = prefersReducedMotion() ? 'auto' : 'smooth';
      if (typeof this.track.scrollTo === 'function') {
        try {
          this.track.scrollTo({ left: left, behavior: behavior });
          return;
        } catch (e) {
          /* navegadores antiguos: salto directo */
        }
      }
      this.track.scrollLeft = left;
    }

    step(direction) {
      var x = this.track.scrollLeft;
      var target = null;
      var i;
      if (direction > 0) {
        for (i = 0; i < this.positions.length; i++) {
          if (this.positions[i].pos > x + 4) {
            target = this.positions[i].pos;
            break;
          }
        }
      } else {
        for (i = this.positions.length - 1; i >= 0; i--) {
          if (this.positions[i].pos < x - 4) {
            target = this.positions[i].pos;
            break;
          }
        }
      }
      if (target !== null) this.scrollToX(target);
    }

    goTo(index) {
      var position = this.positions[index];
      if (position) this.scrollToX(position.pos);
    }

    handleClick(event) {
      var target = event.target && event.target.closest
        ? event.target.closest('[data-lm-vs-prev],[data-lm-vs-next],[data-lm-vs-dot],[data-lm-vs-toggle],[data-lm-vs-play]')
        : null;
      if (!target || !this.contains(target)) return;

      if (target.hasAttribute('data-lm-vs-prev')) {
        this.step(-1);
      } else if (target.hasAttribute('data-lm-vs-next')) {
        this.step(1);
      } else if (target.hasAttribute('data-lm-vs-dot')) {
        this.goTo(parseInt(target.getAttribute('data-lm-vs-dot'), 10));
      } else if (target.hasAttribute('data-lm-vs-toggle')) {
        if (this.wantPlay) this.pauseAll();
        else this.playAll(null, target);
      } else if (target.hasAttribute('data-lm-vs-play')) {
        this.playAll(this.findItem(target.closest('[data-lm-vs-slide]')), target);
      }
    }

    /* Editor de temas: al elegir un bloque, la pista va a su vídeo. */
    handleBlockSelect(event) {
      var blockId = event && event.detail && event.detail.blockId;
      if (!blockId) return;
      for (var i = 0; i < this.slides.length; i++) {
        if (this.slides[i].getAttribute('data-block-id') === blockId) {
          this.scrollToX(this.slidePosition(this.slides[i], this.paddingStart(), this.maxScroll));
          return;
        }
      }
    }
  }

  window.customElements.define('lm-video-slider', LmVideoSlider);
})();
